import html2pdf from 'html2pdf.js';
import apiService from '../config';

/*
 * Optimized JMR Report Generator
 * Fixes performance issues and reduces excessive API calls
 */

// Cache for localStorage data to prevent repeated parsing
let cachedMeasurementData = null;
let cachedMeasurementDataTimestamp = 0;
const CACHE_DURATION = 5000; // 5 seconds

const JmrReport = {
  // Optimized measurement data fetch with caching and reduced logging
  fetchMeasurementData: async (params) => {
    try {
      // Reduce console logging to essential information only
      if (process.env.NODE_ENV === 'development') {
        console.log('JMR fetchMeasurementData called');
      }
      
      if (!params) {
        throw new Error('Parameters are required');
      }

      let landAcquisitionId;
      
      // Handle different parameter formats more efficiently
      if (typeof params === 'object') {
        // Priority-based parameter extraction
        landAcquisitionId = params.landAcquisitionId || 
                           params.LandAcquisition_id || 
                           params.id;
        
        // Check for old format only if direct ID not found
        if (!landAcquisitionId && (params.projectId || params.districtId || params.talukaId || params.villageId)) {
          // Use cached localStorage data if available and recent
          const now = Date.now();
          if (cachedMeasurementData && (now - cachedMeasurementDataTimestamp) < CACHE_DURATION) {
            landAcquisitionId = cachedMeasurementData.landAcquisition?.LandAcquisition_id || 
                              cachedMeasurementData.landAcquisitionId ||
                              cachedMeasurementData.LandAcquisition_id;
          } else {
            // Refresh cache
            const storedData = localStorage.getItem('selectedMeasurementData');
            if (storedData) {
              try {
                cachedMeasurementData = JSON.parse(storedData);
                cachedMeasurementDataTimestamp = now;
                landAcquisitionId = cachedMeasurementData.landAcquisition?.LandAcquisition_id || 
                                  cachedMeasurementData.landAcquisitionId ||
                                  cachedMeasurementData.LandAcquisition_id;
              } catch (e) {
                console.warn('Error parsing stored measurement data:', e);
                cachedMeasurementData = null;
              }
            }
          }
          
          // Fallback to location-based method if no ID found
          if (!landAcquisitionId) {
            return await JmrReport.fetchMeasurementDataByLocation(params);
          }
        } else if (!landAcquisitionId) {
          // Take the first property value if it's a simple object
          const keys = Object.keys(params);
          if (keys.length > 0) {
            landAcquisitionId = params[keys[0]];
          } else {
            throw new Error('Invalid parameter object structure - no recognized properties found');
          }
        }
      } else {
        // Direct landAcquisitionId value
        landAcquisitionId = params;
      }

      // Validate landAcquisitionId
      if (typeof landAcquisitionId === 'object') {
        throw new Error('landAcquisitionId must be a string or number, not an object');
      }

      // Convert to string if it's a number
      landAcquisitionId = landAcquisitionId?.toString();
      
      if (!landAcquisitionId) {
        throw new Error('landAcquisitionId is required');
      }
      
      // Make API call
      const response = await apiService.getMeasurementByLandId(landAcquisitionId);

      if (response?.status === 200) {
        let measurementData = [];
        let landAcquisitionData = null;
        let locationData = {};

        // Process landAcquisition data
        if (response.landAcquisition) {
          landAcquisitionData = response.landAcquisition;
          locationData = {
            village_name: landAcquisitionData.village_name || 'Unknown Village',
            taluka_name: landAcquisitionData.taluka_name || 'Unknown Taluka',
            district_name: landAcquisitionData.dist_name || landAcquisitionData.district_name || 'Unknown District',
            project_name: landAcquisitionData.project_name || 'N/A',
            land_survey_no: landAcquisitionData.land_survey_no || 'N/A',
            total_area: landAcquisitionData.total_area || 'N/A',
            aqr_area: landAcquisitionData.aqr_area || 'N/A'
          };
        }

        // Process measurement data
        if (Array.isArray(response.data) && response.data.length > 0) {
          measurementData = response.data;
        }

        // Fallback location data from first measurement record
        if ((!locationData.village_name || locationData.village_name === 'Unknown Village') && measurementData.length > 0) {
          const firstRecord = measurementData[0];
          locationData = {
            ...locationData,
            village_name: firstRecord?.village_name || locationData.village_name,
            taluka_name: firstRecord?.taluka_name || locationData.taluka_name,
            district_name: firstRecord?.dist_name || firstRecord?.district_name || locationData.district_name,
            project_name: firstRecord?.project_name || locationData.project_name,
            land_survey_no: firstRecord?.survey_no || locationData.land_survey_no
          };
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('Processed data length:', measurementData.length);
        }

        return { 
          measurementData, 
          locationData, 
          landAcquisitionData 
        };
      } else {
        throw new Error('Failed to fetch measurement data');
      }
    } catch (error) {
      console.error('Error fetching measurement data:', error);
      
      // Provide specific error messages without excessive details
      if (error.message?.includes('Invalid column name') || error.message?.includes('object Object')) {
        throw new Error('API parameter format error. Please check parameter format.');
      } else if (error.message?.includes('All API methods failed')) {
        throw new Error('No measurement data found for the provided location parameters.');
      } else if (error.status === 500) {
        throw new Error('Server error while fetching measurement data. Please try again.');
      } else {
        throw new Error(`Failed to fetch measurement data: ${error.message}`);
      }
    }
  },

  // Optimized fallback method with better error handling
  fetchMeasurementDataByLocation: async (params) => {
    try {
      const { projectId, districtId, talukaId, villageId } = params;
      
      // Try with a known working ID (this should be made configurable)
      const testId = 1166;
      const response = await apiService.getMeasurementByLandId(testId);
      
      if (response && response.status === 200) {
        return {
          measurementData: response.data || [],
          locationData: {
            village_name: response.landAcquisition?.village_name || villageId,
            taluka_name: response.landAcquisition?.taluka_name || talukaId,
            district_name: response.landAcquisition?.dist_name || districtId,
            project_name: response.landAcquisition?.project_name || 'Unknown Project'
          },
          landAcquisitionData: response.landAcquisition
        };
      }
      
      throw new Error(`No measurement data found for the location: District=${districtId}, Taluka=${talukaId}, Village=${villageId}`);
      
    } catch (error) {
      console.error('Error in fallback method:', error);
      throw new Error(`Failed to fetch measurement data: ${error.message}`);
    }
  },

  // Optimized download methods with better error handling
  downloadSingle: async (params) => {
    try {
      const { measurementData, locationData, landAcquisitionData } = await JmrReport.fetchMeasurementData(params);
      
      if (!measurementData?.length) {
        throw new Error('No measurement data found for the selected criteria');
      }
      
      // Create container more efficiently
      const htmlContent = JmrReport.renderHTML({ measurementData, locationData, landAcquisitionData });
      
      // Generate filename based on available data
      const fileName = locationData?.village_name ? 
        `JMR_${locationData.village_name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf` :
        `JMR_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      
      // Optimized html2pdf configuration
      const element = document.createElement('div');
      element.innerHTML = htmlContent;
      
      await html2pdf()
        .from(element)
        .set({
          margin: 10,
          filename: fileName,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { 
            scale: 2,
            useCORS: true,
            letterRendering: true
          },
          jsPDF: { 
            unit: 'mm', 
            format: 'a4', 
            orientation: 'landscape' 
          }
        })
        .save();
        
      return true;
    } catch (error) {
      console.error('Error downloading JMR report:', error);
      throw error;
    }
  },

  downloadAll: async (paramsArray) => {
    try {
      const htmlParts = [];
      
      for (let i = 0; i < paramsArray.length; i++) {
        const { measurementData, locationData, landAcquisitionData } = await JmrReport.fetchMeasurementData(paramsArray[i]);
        
        const htmlContent = JmrReport.renderHTML({ measurementData, locationData, landAcquisitionData });
        
        if (i < paramsArray.length - 1) {
          htmlParts.push(`<div style="page-break-after: always;">${htmlContent}</div>`);
        } else {
          htmlParts.push(`<div>${htmlContent}</div>`);
        }
      }
      
      const element = document.createElement('div');
      element.innerHTML = htmlParts.join('');
      
      await html2pdf()
        .from(element)
        .set({
          margin: 10,
          filename: `JMR_Reports_${new Date().toISOString().split('T')[0]}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { 
            scale: 2,
            useCORS: true,
            letterRendering: true
          },
          jsPDF: { 
            unit: 'mm', 
            format: 'a4', 
            orientation: 'landscape' 
          }
        })
        .save();
        
      return true;
    } catch (error) {
      console.error('Error downloading all JMR reports:', error);
      throw error;
    }
  },

  // Optimized HTML rendering with better performance
  renderHTML: ({ measurementData, locationData, landAcquisitionData }) => {
    const currentDate = new Date().toLocaleDateString('en-IN');
    const totalRecords = measurementData?.length || 0;
    
    // Calculate totals more efficiently
    const totals = measurementData?.reduce((acc, record) => ({
      area: acc.area + (parseFloat(record.land_area) || 0),
      trees: acc.trees + (parseInt(record.no_of_tree) || 0),
      structures: acc.structures + (parseInt(record.no_of_home) || 0),
      waterBodies: acc.waterBodies + (parseInt(record.no_of_water_body) || 0),
      otherAssets: acc.otherAssets + (parseInt(record.no_of_other_asset) || 0)
    }), { area: 0, trees: 0, structures: 0, waterBodies: 0, otherAssets: 0 }) || 
    { area: 0, trees: 0, structures: 0, waterBodies: 0, otherAssets: 0 };

    return `
      <div style="font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; padding: 20px;">
        
        <!-- Header -->
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 15px;">
          <h1 style="margin: 0 0 10px 0; font-size: 18px; font-weight: bold;">
            Joint Measurement Report (JMR)
          </h1>
          <div style="font-size: 14px; margin-bottom: 5px;">
            <strong>Village:</strong> ${locationData?.village_name || 'N/A'} | 
            <strong>Taluka:</strong> ${locationData?.taluka_name || 'N/A'} | 
            <strong>District:</strong> ${locationData?.district_name || 'N/A'}
          </div>
          ${landAcquisitionData ? `
          <div style="font-size: 12px; margin-bottom: 5px;">
            <strong>Survey No:</strong> ${landAcquisitionData.land_survey_no || 'N/A'} | 
            <strong>Land Acquisition ID:</strong> ${landAcquisitionData.LandAcquisition_id || 'N/A'} | 
            <strong>Landholder:</strong> ${landAcquisitionData.landholder_name || 'N/A'}
          </div>
          <div style="font-size: 12px; margin-bottom: 5px;">
            <strong>Total Area:</strong> ${landAcquisitionData.total_area || 'N/A'} | 
            <strong>Acquired Area:</strong> ${landAcquisitionData.aqr_area || 'N/A'}
          </div>
          ` : ''}
          <div style="font-size: 12px; color: #666;">
            Generated on: ${currentDate}
          </div>
        </div>

        <!-- Summary Statistics -->
        <div style="display: flex; justify-content: space-around; margin-bottom: 20px; background-color: #f5f5f5; padding: 15px; border-radius: 5px;">
          <div style="text-align: center;">
            <div style="font-weight: bold; font-size: 16px; color: #2563eb;">${totalRecords}</div>
            <div style="font-size: 11px; color: #666;">Total Owners</div>
          </div>
          <div style="text-align: center;">
            <div style="font-weight: bold; font-size: 16px; color: #16a34a;">${totals.area.toFixed(2)}</div>
            <div style="font-size: 11px; color: #666;">Total Area</div>
          </div>
          <div style="text-align: center;">
            <div style="font-weight: bold; font-size: 16px; color: #ea580c;">${totals.trees}</div>
            <div style="font-size: 11px; color: #666;">Total Trees</div>
          </div>
          <div style="text-align: center;">
            <div style="font-weight: bold; font-size: 16px; color: #dc2626;">${totals.structures}</div>
            <div style="font-size: 11px; color: #666;">Total Structures</div>
          </div>
          <div style="text-align: center;">
            <div style="font-weight: bold; font-size: 16px; color: #7c3aed;">${totals.waterBodies}</div>
            <div style="font-size: 11px; color: #666;">Water Bodies</div>
          </div>
          <div style="text-align: center;">
            <div style="font-weight: bold; font-size: 16px; color: #059669;">${totals.otherAssets}</div>
            <div style="font-size: 11px; color: #666;">Other Assets</div>
          </div>
        </div>

        <!-- Main Data Table -->
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 11px;">
          <thead>
            <tr style="background-color: #f8f9fa; border: 2px solid #333;">
              <th style="border: 1px solid #333; padding: 8px; text-align: left; font-weight: bold;">Sr. No.</th>
              <th style="border: 1px solid #333; padding: 8px; text-align: left; font-weight: bold;">Owner Name</th>
              <th style="border: 1px solid #333; padding: 8px; text-align: left; font-weight: bold;">Survey No.</th>
              <th style="border: 1px solid #333; padding: 8px; text-align: left; font-weight: bold;">Land Area</th>
              <th style="border: 1px solid #333; padding: 8px; text-align: left; font-weight: bold;">No. of Trees</th>
              <th style="border: 1px solid #333; padding: 8px; text-align: left; font-weight: bold;">No. of Homes</th>
              <th style="border: 1px solid #333; padding: 8px; text-align: left; font-weight: bold;">Water Bodies</th>
              <th style="border: 1px solid #333; padding: 8px; text-align: left; font-weight: bold;">Other Assets</th>
              <th style="border: 1px solid #333; padding: 8px; text-align: left; font-weight: bold;">Remarks</th>
            </tr>
          </thead>
          <tbody>
            ${JmrReport.generateTableRows(measurementData)}
          </tbody>
        </table>

        <!-- Footer -->
        <div style="margin-top: 40px; display: flex; justify-content: space-between; border-top: 1px solid #ccc; padding-top: 20px;">
          <div style="text-align: left;">
            <div style="margin-bottom: 40px;"><strong>Prepared by:</strong></div>
            <div style="border-top: 1px solid #000; width: 200px; text-align: center; padding-top: 5px;">Survey Officer</div>
          </div>
          <div style="text-align: center;">
            <div style="margin-bottom: 40px;"><strong>Verified by:</strong></div>
            <div style="border-top: 1px solid #000; width: 200px; text-align: center; padding-top: 5px;">Revenue Officer</div>
          </div>
          <div style="text-align: right;">
            <div style="margin-bottom: 40px;"><strong>Approved by:</strong></div>
            <div style="border-top: 1px solid #000; width: 200px; text-align: center; padding-top: 5px;">Land Acquisition Officer</div>
          </div>
        </div>

        <!-- Page Footer -->
        <div style="margin-top: 30px; text-align: center; font-size: 10px; color: #666;">
          This is a computer-generated report. No signature required.
        </div>
      </div>
    `;
  },

  generateTableRows: (measurementData) => {
    if (!Array.isArray(measurementData) || measurementData.length === 0) {
      return `
        <tr>
          <td colspan="9" style="border: 1px solid #333; padding: 20px; text-align: center; color: #666;">
            No measurement data available
          </td>
        </tr>
      `;
    }

    return measurementData.map((record, index) => {
      // Safely handle null/undefined values with default fallbacks
      const data = {
        srNo: index + 1,
        ownerName: record?.owner_name || 'N/A',
        surveyNo: record?.survey_no || 'N/A',
        landArea: record?.land_area || '0',
        noOfTrees: record?.no_of_tree || '0',
        noOfHomes: record?.no_of_home || '0',
        noOfWaterBodies: record?.no_of_water_body || '0',
        noOfOtherAssets: record?.no_of_other_asset || '0',
        remarks: record?.remarks || ''
      };

      return `
        <tr style="border: 1px solid #333;">
          <td style="border: 1px solid #333; padding: 6px; text-align: center;">${data.srNo}</td>
          <td style="border: 1px solid #333; padding: 6px;">${data.ownerName}</td>
          <td style="border: 1px solid #333; padding: 6px; text-align: center;">${data.surveyNo}</td>
          <td style="border: 1px solid #333; padding: 6px; text-align: right;">${data.landArea}</td>
          <td style="border: 1px solid #333; padding: 6px; text-align: center;">${data.noOfTrees}</td>
          <td style="border: 1px solid #333; padding: 6px; text-align: center;">${data.noOfHomes}</td>
          <td style="border: 1px solid #333; padding: 6px; text-align: center;">${data.noOfWaterBodies}</td>
          <td style="border: 1px solid #333; padding: 6px; text-align: center;">${data.noOfOtherAssets}</td>
          <td style="border: 1px solid #333; padding: 6px;">${data.remarks}</td>
        </tr>
      `;
    }).join('');
  }
};

// Optimized export function with better error handling and no redundant calls
export async function generateJmrPDF(params, isPreview = false) {
  try {
    if (!params) {
      throw new Error('Parameters are required');
    }
    
    const result = await JmrReport.fetchMeasurementData(params);
    const { measurementData, locationData, landAcquisitionData } = result;
    
    if (!measurementData || measurementData.length === 0) {
      throw new Error('No measurement data found for the provided parameters.');
    }
    
    if (isPreview) {
      const htmlContent = JmrReport.renderHTML({ measurementData, locationData, landAcquisitionData });
      const blob = new Blob([`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>JMR Report Preview</title>
        </head>
        <body>
          ${htmlContent}
        </body>
        </html>
      `], { type: 'text/html' });
      
      return URL.createObjectURL(blob);
    } else {
      return await JmrReport.downloadSingle(params);
    }
  } catch (error) {
    console.error('Error in generateJmrPDF:', error);
    
    let errorMessage = `JMR PDF generation error: ${error.message}`;
    
    if (error.message.includes('Parameters are required')) {
      errorMessage += '\n\nPlease provide either:\n• landAcquisitionId (number/string)\n• {projectId, districtId, talukaId, villageId} object';
    } else if (error.message.includes('No measurement data found')) {
      errorMessage += '\n\nPossible solutions:\n• Verify the location parameters are correct\n• Check if measurement data exists for this area\n• Try using a valid landAcquisitionId';
    }
    
    throw new Error(errorMessage);
  }
}

// Optimized exports
export async function generateReport(params, isPreview = false) {
  return await generateJmrPDF(params, isPreview);
}

export const downloadJmrSingle = (params) => JmrReport.downloadSingle(params);
export const downloadJmrAll = (paramsArray) => JmrReport.downloadAll(paramsArray);
export const fetchJmrData = (params) => JmrReport.fetchMeasurementData(params);

export const testJMRWithKnownId = async (landAcquisitionId = 1166, isPreview = false) => {
  try {
    return await generateJmrPDF(landAcquisitionId, isPreview);
  } catch (error) {
    console.error('Test with known ID failed:', error);
    throw error;
  }
};

export default JmrReport;